import React, { useState, useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useLanguage } from '@/context/LanguageContext';
import { 
  FormField, FormItem, FormLabel, FormControl, FormMessage 
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { countries } from '@/utils/countries';
import { enhancedInputValidator } from '@/utils/enhancedInputValidator';
import '@/styles/emoji-flags.css';

// Parse date string as local date to avoid timezone issues
const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

interface UserInfoSectionProps {
  form: UseFormReturn<any>;
  onUsernameBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const UserInfoSection = ({ form, onUsernameBlur }: UserInfoSectionProps) => {
  const { t } = useLanguage();
  const [flagsSupported, setFlagsSupported] = useState<boolean>(true);
  const [calendarView, setCalendarView] = useState<'day' | 'month' | 'year'>('year');
  const [viewDate, setViewDate] = useState<Date>(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    const testElement = document.createElement('span');
    testElement.textContent = '🇺🇸';
    testElement.style.visibility = 'hidden';
    testElement.style.position = 'absolute';
    document.body.appendChild(testElement);
    const supportsFlags = testElement.offsetWidth > 0;
    document.body.removeChild(testElement);
    setFlagsSupported(supportsFlags);
  }, []);

  const sortedCountries = useMemo(() => {
    return [...countries].sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const uniqueCountryOptions = useMemo(() => {
    const uniqueOptions: Array<{
      value: string;
      label: string;
      flag: string;
      country: string;
      dialCode: string;
    }> = [];
    
    sortedCountries.forEach(country => {
      uniqueOptions.push({
        value: `${country.code}-${country.dial_code}`,
        label: `${country.name} (${country.dial_code})`,
        flag: country.flag,
        country: country.name,
        dialCode: country.dial_code
      });
    });
    
    return uniqueOptions.sort((a, b) => a.country.localeCompare(b.country));
  }, [sortedCountries]);

  const getTranslatedMonthYear = (date: Date) => {
    const monthKeys = [
      'calendar.january', 'calendar.february', 'calendar.march', 'calendar.april',
      'calendar.may', 'calendar.june', 'calendar.july', 'calendar.august',
      'calendar.september', 'calendar.october', 'calendar.november', 'calendar.december'
    ];
    const monthIndex = date.getMonth();
    const year = date.getFullYear();
    return `${t(monthKeys[monthIndex])} ${year}`;
  };

  const navigateCalendar = (direction: 'prev' | 'next') => {
    const newDate = new Date(viewDate);
    
    if (calendarView === 'day') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    } else if (calendarView === 'month') {
      newDate.setFullYear(newDate.getFullYear() + (direction === 'next' ? 1 : -1));
    } else if (calendarView === 'year') {
      newDate.setFullYear(newDate.getFullYear() + (direction === 'next' ? 10 : -10));
    }
    
    setViewDate(newDate);
  };

  const generateCalendarDays = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const generateMonths = () => {
    const monthKeys = [
      'calendar.months.january', 'calendar.months.february', 'calendar.months.march', 'calendar.months.april',
      'calendar.months.may', 'calendar.months.june', 'calendar.months.july', 'calendar.months.august', 
      'calendar.months.september', 'calendar.months.october', 'calendar.months.november', 'calendar.months.december'
    ];
    
    const fullMonthKeys = [
      'calendar.january', 'calendar.february', 'calendar.march', 'calendar.april',
      'calendar.may', 'calendar.june', 'calendar.july', 'calendar.august',
      'calendar.september', 'calendar.october', 'calendar.november', 'calendar.december'
    ];
    
    const months = [];
    for (let i = 0; i < 12; i++) {
      months.push({
        value: i,
        label: t(monthKeys[i]),        // 3-letter abbreviation
        fullLabel: t(fullMonthKeys[i]) // Full month name
      });
    }
    return months;
  };

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const latestYear = currentYear - 16;
    const startYear = latestYear - 84;
    const years = [];
    
    for (let i = latestYear; i >= startYear; i--) {
      years.push(i);
    }
    
    return years;
  };

  const handleDateSelect = (day: number) => {
    const selectedDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    const today = new Date();
    const eighteenYearsAgo = new Date(
      today.getFullYear() - 18,
      today.getMonth(),
      today.getDate()
    );
    
    if (selectedDate <= eighteenYearsAgo) {
      form.setValue('date_of_birth', format(selectedDate, 'yyyy-MM-dd'));
      setCalendarOpen(false);
    }
  };

  const handleMonthSelect = (month: number) => {
    const newDate = new Date(viewDate.getFullYear(), month, 1);
    setViewDate(newDate);
    setCalendarView('day');
  };

  const handleYearSelect = (year: number) => {
    const newDate = new Date(year, viewDate.getMonth(), 1);
    setViewDate(newDate);
    setCalendarView('month');
  };

  const handleCalendarOpen = (open: boolean) => {
    setCalendarOpen(open);
    if (open) {
      const selectedDate = form.watch('date_of_birth');
      if (selectedDate) {
        const date = parseLocalDate(selectedDate);
        setViewDate(date);
        setCalendarView('day');
      } else {
        setCalendarView('year');
      }
    }
  };

  const handleCountryCodeChange = (value: string) => {
    const selectedOption = uniqueCountryOptions.find(opt => opt.value === value);
    if (selectedOption) {
      form.setValue('country_code', selectedOption.dialCode);
    }
  };

  const selectedDate = form.watch('date_of_birth') ? parseLocalDate(form.watch('date_of_birth')) : null;
  const selectedCountryCode = form.watch('country_code');

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-neutral-300">{t('auth.userInfo')}</h3>
      
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <FormField
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-neutral-300">{t('auth.firstName')}</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    autoComplete="given-name"
                    className="bg-white/10 border-white/20 text-white placeholder:text-neutral-400 h-10"
                    onChange={(e) => {
                      const filtered = e.target.value.replace(/[^a-zA-ZÀ-ÿ\s'-]/g, '').substring(0, 50);
                      field.onChange(filtered);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-neutral-300">{t('auth.lastName')}</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    autoComplete="family-name"
                    className="bg-white/10 border-white/20 text-white placeholder:text-neutral-400 h-10"
                    onChange={(e) => {
                      const filtered = e.target.value.replace(/[^a-zA-ZÀ-ÿ\s'-]/g, '').substring(0, 50);
                      field.onChange(filtered);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-neutral-300">{t('auth.username')}</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    autoComplete="username"
                    className="bg-white/10 border-white/20 text-white placeholder:text-neutral-400 h-10"
                    onChange={(e) => {
                      const sanitized = enhancedInputValidator.sanitizeText(e.target.value, 20);
                      field.onChange(sanitized);
                      form.clearErrors('username');
                    }}
                    onBlur={(e) => {
                      field.onBlur();
                      onUsernameBlur(e);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="gender"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-neutral-300">{t('auth.gender')}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger translate="no" className="bg-white/10 border-white/20 text-white h-10">
                      <SelectValue placeholder={t('auth.selectGender')} className="text-neutral-400" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent translate="no" className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="male" className="text-white focus:bg-gray-700 focus:text-white">{t('auth.male')}</SelectItem>
                    <SelectItem value="female" className="text-white focus:bg-gray-700 focus:text-white">{t('auth.female')}</SelectItem>
                    <SelectItem value="other" className="text-white focus:bg-gray-700 focus:text-white">{t('auth.other')}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <FormField
            control={form.control}
            name="date_of_birth"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-neutral-300">{t('auth.dateOfBirth')}</FormLabel>
                <Popover open={calendarOpen} onOpenChange={handleCalendarOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        translate="no"
                        className={cn(
                          "w-full pl-3 text-left font-normal bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white h-10",
                          !field.value && "text-neutral-400"
                        )}
                      >
                        {field.value ? (
                          format(parseLocalDate(field.value), "PPP")
                        ) : (
                          <span translate="no" className="text-neutral-400">{t('auth.selectDate')}</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-gray-800 border-gray-700 text-white" align="start">
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigateCalendar('prev')}
                          className="text-white hover:bg-gray-700 hover:text-white"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        
                        <div className="flex gap-2">
                          {calendarView !== 'year' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCalendarView(calendarView === 'day' ? 'month' : 'year')}
                              className="text-white hover:bg-gray-700 hover:text-white"
                            >
                              {calendarView === 'day' 
                                ? getTranslatedMonthYear(viewDate)
                                : viewDate.getFullYear()
                              }
                            </Button>
                          )}
                          {calendarView === 'year' && (
                            <span className="text-white px-3 py-1">
                              {t('auth.selectYear')}
                            </span>
                          )}
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigateCalendar('next')}
                          className="text-white hover:bg-gray-700 hover:text-white"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>

                      {calendarView === 'day' && (
                        <div>
                          <div className="grid grid-cols-7 gap-1 mb-2">
                            {[
                              t('calendar.weekdays.sunday'),
                              t('calendar.weekdays.monday'), 
                              t('calendar.weekdays.tuesday'),
                              t('calendar.weekdays.wednesday'),
                              t('calendar.weekdays.thursday'),
                              t('calendar.weekdays.friday'),
                              t('calendar.weekdays.saturday')
                            ].map((day, index) => (
                              <div key={index} className="text-center text-sm font-medium text-gray-400 p-2">
                                {day}
                              </div>
                            ))}
                          </div>
                          <div translate="no" className="grid grid-cols-7 gap-1">
                            {generateCalendarDays().map((day, index) => {
                              if (day === null) {
                                return <div key={index} className="p-2"></div>;
                              }
                              
                              const dayDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
                              const today = new Date();
                              const eighteenYearsAgo = new Date(
                                today.getFullYear() - 18,
                                today.getMonth(),
                                today.getDate()
                              );
                              const isDisabled = dayDate > eighteenYearsAgo;
                              const isSelected = selectedDate && 
                                selectedDate.getDate() === day &&
                                selectedDate.getMonth() === viewDate.getMonth() &&
                                selectedDate.getFullYear() === viewDate.getFullYear();
                              
                              return (
                                <Button
                                  key={day}
                                  variant="ghost"
                                  size="sm"
                                  disabled={isDisabled}
                                  onClick={() => handleDateSelect(day)}
                                  className={cn(
                                    "p-2 text-white hover:bg-gray-700 hover:text-white",
                                    isSelected && "bg-finance-800 hover:bg-finance-900",
                                    isDisabled && "text-gray-500 cursor-not-allowed"
                                  )}
                                >
                                  {day}
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {calendarView === 'month' && (
                        <div className="grid grid-cols-3 gap-2">
                          {generateMonths().map(month => (
                            <Button
                              key={month.value}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMonthSelect(month.value)}
                              className="text-white hover:bg-gray-700 hover:text-white"
                            >
                              {month.label}
                            </Button>
                          ))}
                        </div>
                      )}

                      {calendarView === 'year' && (
                        <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                          {generateYears().map(year => (
                            <Button
                              key={year}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleYearSelect(year)}
                              className="text-white hover:bg-gray-700 hover:text-white"
                            >
                              {year}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-neutral-300">{t('auth.country')}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger translate="no" className="bg-white/10 border-white/20 text-white h-10">
                      <SelectValue placeholder={t('auth.selectCountry')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="max-h-[300px] z-[9999] overflow-y-auto bg-gray-800 border-gray-700 text-white">
                    {sortedCountries.map((country) => (
                      <SelectItem key={country.code} value={country.name} className="text-white focus:bg-gray-700 focus:text-white">
                        <span className="flex items-center">
                          <span className="mr-2 emoji-flag">{country.flag}</span>
                          {country.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          <FormField
            control={form.control}
            name="country_code"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-neutral-300">{t('auth.countryCode')}</FormLabel>
                <Select onValueChange={handleCountryCodeChange} value={uniqueCountryOptions.find(opt => opt.dialCode === selectedCountryCode)?.value}>
                  <FormControl>
                    <SelectTrigger translate="no" className="bg-white/10 border-white/20 text-white h-10">
                      <SelectValue placeholder={t('auth.selectCountryCode')}>
                        {selectedCountryCode && (
                          <span className="flex items-center">
                            {(() => {
                              const option = uniqueCountryOptions.find(opt => opt.dialCode === selectedCountryCode);
                              return option ? (
                                <>
                                  <span className="mr-2 emoji-flag">{option.flag}</span>
                                  {option.dialCode}
                                </>
                              ) : selectedCountryCode;
                            })()}
                          </span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="max-h-[300px] z-[9999] overflow-y-auto bg-gray-800 border-gray-700 text-white">
                    {uniqueCountryOptions.map((option, index) => (
                      <SelectItem 
                        key={`${option.value}-${index}`} 
                        value={option.value} 
                        className="text-white focus:bg-gray-700 focus:text-white"
                      >
                        <span className="flex items-center">
                          <span className="mr-2 emoji-flag">{option.flag}</span>
                          {option.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem className="md:col-span-2 space-y-2">
                <FormLabel className="text-neutral-300">{t('auth.phoneNumber')}</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    type="tel" 
                    autoComplete="tel"
                    className="bg-white/10 border-white/20 text-white placeholder:text-neutral-400 h-10"
                    onChange={(e) => {
                      const sanitized = e.target.value.replace(/\D/g, '');
                      field.onChange(sanitized);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default UserInfoSection;
